import { useMutation } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { UpdateUserProfileDocument } from '../../graphql/generated';
import { DateRange } from '../../types';
import { sortBy } from '../../util/arrays';
import { dt } from '../../util/dateTime';
import { debounce } from '../../util/debounce';
import { useCurrentUser } from '../user';
import { CashflowTransactionEntry } from './types';

const DELAY = 5000;

export const useUpdateLastViewedTransactionEntry = ({
    transactionEntries,
    dateRange,
    areFiltersApplied,
}: {
    transactionEntries: CashflowTransactionEntry[];
    dateRange: DateRange;
    areFiltersApplied: boolean;
}) => {
    const { userInfo } = useCurrentUser();
    const [updateUserProfile] = useMutation(UpdateUserProfileDocument);

    const debouncedUpdate = useMemo(
        () =>
            debounce(async (latestTransactionEntryId: string) => {
                await updateUserProfile({
                    variables: {
                        patch: {
                            lastViewedTransactionEntryId:
                                latestTransactionEntryId,
                        },
                    },
                });
            }, DELAY),
        [updateUserProfile]
    );

    useEffect(() => {
        const now = dt().toDateStr();
        const isCurrentDateInRange =
            dateRange.startDate <= now && dateRange.endDate >= now;

        // Only update the last viewed transaction if:
        // - the user is viewing a date range that includes the current date
        // - no filters are applied
        if (!isCurrentDateInRange || areFiltersApplied) {
            return;
        }

        const latestTransactionEntry = sortBy(
            transactionEntries,
            'date',
            'DESC'
        ).at(0);

        if (!latestTransactionEntry) {
            return;
        }

        // Don't update if
        // - the last viewed transaction is already the latest
        // - or the last viewed transaction is newer than the latest transaction in the view
        if (
            userInfo.lastViewedTransactionEntry &&
            (latestTransactionEntry.id ===
                userInfo.lastViewedTransactionEntry.id ||
                userInfo.lastViewedTransactionEntry.date >
                    latestTransactionEntry.date)
        ) {
            return;
        }

        debouncedUpdate(latestTransactionEntry.id);
    }, [
        debouncedUpdate,
        transactionEntries,
        dateRange,
        areFiltersApplied,
        userInfo.lastViewedTransactionEntry,
    ]);
};
